import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { LoadingService } from '../services/loading.service';

/**
 * Endpoints that should NOT trigger the global loading spinner.
 * These are typically background polling or refresh requests.
 */
import { HttpContextToken } from '@angular/common/http';

export const SKIP_LOADING = new HttpContextToken<boolean>(() => false);

/**
 * Endpoints that should NOT trigger the global loading spinner.
 * These are typically background polling or refresh requests.
 */
export const LOADING_EXCLUDED_ENDPOINTS = new HttpContextToken<string[]>(() => [
  // Background notification polling
  '/in-app-notifications', // Background notification polling (main endpoint)
  '/notifications', // Background notification polling (alias)
  '/messages/notifications', // Message notification polling
  '/messages/stats', // Message stats background polling
  '/messages/unread-count', // Unread count background polling
  
  // Authentication (silent background operations)
  '/auth/refresh', // Token refresh requests
  '/auth/token-exchange', // Token exchange
  '/auth/login', // Login requests - has local spinner
  '/auth/verify-otp', // OTP verification - has local spinner
  '/auth/resend-otp', // Resend OTP - has local spinner
  
  // Table and Form data endpoints - these pages have inline loading spinners
  // Adding them here prevents double spinners (table spinner + global overlay)
  '/hospital-users', // Hospital users list - has inline table loader
  '/internal-users', // Internal users list - has inline table loader
  '/hospitals', // Hospitals list - has inline table loader
  '/programs', // Programs list - has inline table loader
  '/referrals', // Referral list and details - has inline loaders
  '/referral-forms', // Referral forms list - has inline loaders
  '/patients', // Patient search and selection - has inline loaders
]);

/**
 * Helper to check if a URL matches any of the excluded patterns
 */
function isUrlExcluded(url: string, patterns: string[]): boolean {
  return patterns.some((pattern) => url.includes(pattern));
}

/**
 * Check if a URL should be excluded from loading indicator
 */
// Function removed as it is replaced by isUrlExcluded with injected patterns


/**
 * HTTP Interceptor that automatically manages global loading state.
 *
 * Shows the loading spinner when HTTP requests start and hides it when they complete.
 * Handles concurrent requests properly by using a counter-based approach.
 *
 * Excludes certain background endpoints from triggering the spinner.
 */
export const loadingInterceptor: HttpInterceptorFn = (req, next) => {
  const loadingService = inject(LoadingService);

  // Get excluded endpoints from context or use default empty array (though token defaults are set)
  const excludedEndpoints = req.context.get(LOADING_EXCLUDED_ENDPOINTS);

  // Skip loading indicator if SKIP_LOADING context is present or URL is excluded
  if (req.context.get(SKIP_LOADING) || isUrlExcluded(req.url, excludedEndpoints)) {
    return next(req);
  }

  // Show loading spinner
  loadingService.show();

  return next(req).pipe(
    finalize(() => {
      // Hide loading spinner when request completes (success or error)
      loadingService.hide();
    })
  );
};
